import { useQuery } from '@tanstack/react-query';
import { ReconciliationService } from '@/api';

export const paymentApplicationsKeys = {
  all: ['reconciliation', 'applications'] as const,
  byInvoice: (invoiceId: string | null) =>
    [...paymentApplicationsKeys.all, 'by-invoice', invoiceId] as const,
};

// usePaymentApplicationsByInvoice fetches every non-deleted payment_application
// that touches any split of the given invoice. Powers the InvoicesPage
// "View payments" side-sheet. Short-circuits when no invoiceId is selected.
//
// FRESHNESS POLICY (#1338) — see the FRESHNESS POLICY block at the top of
// `useDebtorAccountsList.ts` for the reasoning; this records this hook's call.
//
// Focus refetch is ON. The read is scoped to a single invoice's splits, so its
// cost is bounded by one invoice and does not grow with the org's history —
// the load objection that keeps the org-wide register off does not reach here.
// The sheet answers "what has been paid against this invoice", which is the
// question an operator asks immediately before deciding whether to chase it;
// a payment applied elsewhere while the tab sat in the background must not
// leave that answer wrong.
//
// staleTime is written out rather than inherited so the window reads as
// chosen. The value is the app default and is unchanged by this issue.
export function usePaymentApplicationsByInvoice(invoiceId: string | null) {
  return useQuery({
    queryKey: paymentApplicationsKeys.byInvoice(invoiceId),
    queryFn: () =>
      ReconciliationService.getApiV1ReconciliationInvoicesPaymentApplications(invoiceId as string),
    enabled: !!invoiceId,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
}
